import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyableIdCell from '../../../common/CopyableIdCell.js';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from '@mui/material/CircularProgress';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import StableMuiDialog from '../../../common/StableMuiDialog.js';
import { parseMLSPhotoFolder } from '../Photos/photoUrls';
import { usePhotoActions } from '../Photos/usePhotoActions';
import { usePhotoCarousel } from '../Photos/usePhotoCarousel';
import { usePhotoImage } from '../Photos/usePhotoImage';
import { useDragAndDrop } from '../Photos/useDragAndDrop';
import { PhotoOverlayArrows, PhotoCountBadge } from '../Photos/PhotoNavArrows';
import PhotoUploadDialog from '../Photos/PhotoUploadDialog';
import PhotoDeleteConfirmDialog from '../Photos/PhotoDeleteConfirmDialog';
import PhotoDetailDialog from '../Photos/PhotoDetailDialog';
import { handleCopy } from '../../../common/helper_functions';
import { flagSingleBadSale } from '../../../common/flagBadSaleHelper';
import FlagBadSaleDialog from '../FlagBadSaleDialog.js';
import { usePersistedDataStore } from '../../store';
import { useLocation } from 'react-router-dom';
import { userObject } from '../../UserContext';
import { STATE } from '../../../common/mappings';
import CaseNotesInput from '../CaseNotesInput';
import TooltipCheckbox from './TooltipCheckbox';
import { 
  resetAllStyling 
} from './mapStyleUtils';
import { formatDateWithoutTimezone } from '../../../common/dateUtils';
import { fetchRarHistory } from '../utils/calculatedTav';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { updateOffersBatch } from '../SummaryCaseTable/services/realtimeOfferService';
import { createOfferContext } from '../../../utils/offerContextUtils';
import { batchSaveComps, normalizeCaseNotes, resolveCaseNotesForCase } from '../SaveComps/batchSaveComps';
import { createSaveObject } from '../SaveComps/createSaveObject';
import { useUser } from '../../UserContext';
import { getConversionFactor } from '../../../utils/conversionUtils';
import logger from '../../../utils/logger';
import axiosInstance from '../../../fetchConfig/axiosConfig';
import { toast } from '../../../common/toast';


const isYesFlag = (v) => v === 1 || v === '1' || v === true || v === 'True';

const displayInventory = (v, kind) => {
  if (kind === 'flag') return isYesFlag(v) ? 'Yes' : 'None';
  if (kind === 'cac') {
    if (isYesFlag(v)) return 'Yes';
    if (v === 0 || v === '0' || v === false || v === 'False') return 'No';
    return '—';
  }
  if (v === null || v === undefined || v === '') return '—';
  if (kind === 'year') {
    const n = Number(String(v).replace('*', ''));
    return Number.isFinite(n) && n > 0 ? String(Math.round(n)) : '—';
  }
  if (kind === 'acres') {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : '—';
  }
  if (typeof v === 'number' && Number.isFinite(v)) return v.toLocaleString();
  const n = Number(v);
  if (Number.isFinite(n) && !/[a-zA-Z]/.test(String(v))) return n.toLocaleString();
  return String(v);
};

const outdoorLabel = (deck, patio, porch) => {
  const parts = [isYesFlag(deck) && 'Deck', isYesFlag(patio) && 'Patio', isYesFlag(porch) && 'Porch'].filter(Boolean);
  return parts.length ? parts.join(', ') : 'None';
};

// Add StreetView metadata cache
const streetViewCache = {
  cache: new Map(),
  
  getKey(lat, lng) {
    return `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
  },

  getAvailability(lat, lng) {
    const key = this.getKey(lat, lng);
    const result = this.cache.get(key);
    return result;
  },

  setAvailability(lat, lng, data) {
    const key = this.getKey(lat, lng);
    this.cache.set(key, data);
  }
};

// Add StreetView component
const StreetView = ({ lat, lng, panoId, onClose }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [isLoading, setIsLoading] = useState(true);

  // If we don't have valid coordinates, don't render anything
  if (!lat || !lng) {
    return null;
  }

  // Construct URL with either pano_id or lat/lng
  const url = panoId 
    ? `https://www.google.com/maps/embed/v1/streetview?key=${apiKey}&pano=${panoId}&heading=210&pitch=10&fov=90`
    : `https://www.google.com/maps/embed/v1/streetview?key=${apiKey}&location=${lat},${lng}&heading=210&pitch=10&fov=90`;

  return (
    <div className="absolute top-0 right-0 h-full w-full bg-white shadow-lg overflow-hidden">
      <div className="relative w-full h-full">
        {isLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        )}
        <iframe
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen
          src={url}
          onLoad={() => setIsLoading(false)}
        />
        <button
          className="absolute top-2 right-2 w-11 h-11 bg-[#444444] flex items-center justify-center hover:bg-[#555555]"
          onClick={onClose}
        >
          <CloseIcon className="text-white text-xl" />
        </button>
      </div>
    </div>
  );
};

// Negotiations Notes Input Component
const NegotiationsNotesInput = ({ caseObject, selectedProperty }) => {
  const [aventineNote, setAventineNote] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAV, setShowAV] = useState({}); // Track which rounds show AV instead of MV
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const debounceTimerRef = useRef(null);

  // Get negotiations data
  const negotiations = caseObject?.Negotiations || [];
  


  // Sort negotiations by round number (descending - most recent first)
  const sortedNegotiations = [...negotiations].sort((a, b) => (b.round || 0) - (a.round || 0));
  const currentRound = negotiations.length > 0 ? Math.max(...negotiations.map(n => n.round)) : 1;
  const currentRoundData = negotiations.find(n => n.round === currentRound);
  const aventineOffer = currentRoundData?.offers?.find(offer => offer.OfferedBy === 'Aventine');

  // Get conversion factor for MV/AV conversion
  if (!caseObject) throw new Error('[PropertyRecordCard] Missing caseObject for conversion.');
  const conversionFactor = getConversionFactor(caseObject.EQ, caseObject.RAR);
  
  // Get IFMV for % reduction calculation
  const ifmv = selectedProperty?.ifmv || 0;

  // Initialize notes from negotiations
  useEffect(() => {
    setAventineNote(aventineOffer?.Comments || '');
  }, [aventineOffer?.Comments]);

  const handleNoteChange = useCallback((value) => {
    setAventineNote(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced update
    debounceTimerRef.current = setTimeout(async () => {
      if (isUpdating) return;
      
      setIsUpdating(true);
      
      try {
        const offer = aventineOffer;
        
        // Get update context
        const user = {
          userId: getNegotiationObj?.UserId,
          userAccess: getNegotiationObj?.UserAccess?.level || 'Aventine'
        };
        const context = createOfferContext(user, getNegotiationObj);

        const trimmedValue = value.trim();
        const offerForBatch = {
          parcelId: selectedProperty.parcel_id,
          offerValue: offer?.Offer || null,
          offeredBy: 'Aventine',
          offerDate: new Date().toISOString(),
          comments: trimmedValue,
          offerId: offer?.OfferID,
        };
        
        await updateOffersBatch([offerForBatch], context, { 
          updateFrontend: true,
          round: currentRound
        });
        
        logger.log(`✅ Successfully updated Aventine note`);
      } catch (error) {
        logger.error(`❌ Error updating Aventine note:`, error);
      } finally {
        setIsUpdating(false);
      }
    }, 1000); // 1 second debounce
  }, [aventineOffer, currentRound, selectedProperty.parcel_id, getNegotiationObj, isUpdating]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Format offer value for display: Market Value or AV
  const formatOffer = (offer, showAVValue = false) => {
    if (!offer || offer.Offer === null || offer.Offer === undefined) return null;
    
    const avValue = Number(offer.Offer);
    if (isNaN(avValue) || avValue <= 0) return null;
    
    if (showAVValue) {
      return `${avValue.toLocaleString()} AV`;
    }
    
    // Convert AV to MV
    const marketValue = conversionFactor > 0 ? avValue / conversionFactor : avValue;
    
    return `$${Math.round(marketValue).toLocaleString()}`;
  };

  // Calculate % reduction
  const calculateReduction = (offer, ifmvValue = ifmv) => {
    if (!offer || offer.Offer === null || offer.Offer === undefined || ifmvValue <= 0) return null;
    
    const avValue = Number(offer.Offer);
    if (isNaN(avValue) || avValue <= 0) return null;
    
    const marketValue = conversionFactor > 0 ? avValue / conversionFactor : avValue;
    const reduction = ((ifmvValue - marketValue) / ifmvValue) * 100;
    
    // Format: don't show + for 0, show - for positive reduction, + for negative
    let display = '';
    if (Math.abs(reduction) < 0.05) {
      display = '0%';
    } else if (reduction > 0) {
      display = `-${reduction.toFixed(1)}%`;
    } else {
      display = `+${Math.abs(reduction).toFixed(1)}%`;
    }
    
    return {
      value: reduction,
      display: display,
      isNegative: reduction < 0 // Negative reduction is bad (we're asking for more than IFMV)
    };
  };

  // Toggle AV/MV display for a round and party
  const toggleAV = (roundNumber, party) => {
    const key = `${roundNumber}-${party}`;
    setShowAV(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Get comment preview (first 50 characters)
  const getCommentPreview = (comment) => {
    if (!comment || !comment.trim()) return null;
    const trimmed = comment.trim();
    return trimmed.length > 50 ? `${trimmed.substring(0, 50)}...` : trimmed;
  };

  return (
    <div className="flex flex-col flex-grow relative min-h-0">
      {/* Display all rounds */}
      {sortedNegotiations.length > 0 ? (
        <div className="space-y-2 mb-2 flex-1 overflow-y-auto pb-0 min-h-0">
          {sortedNegotiations.map((roundData) => {
            const roundAventineOffer = roundData.offers?.find(o => o.OfferedBy === 'Aventine');
            const roundTownOffer = roundData.offers?.find(o => o.OfferedBy === 'Town');
            const isCurrentRound = roundData.round === currentRound;
            
            // Condensed view for older rounds
            if (!isCurrentRound) {
              const showAventineAV = showAV[`${roundData.round}-Aventine`];
              const showTownAV = showAV[`${roundData.round}-Town`];
              const aventineOfferValue = formatOffer(roundAventineOffer, showAventineAV);
              const townOfferValue = formatOffer(roundTownOffer, showTownAV);
              const aventineComment = roundAventineOffer?.Comments?.trim();
              const townComment = roundTownOffer?.Comments?.trim();
              const aventineReduction = calculateReduction(roundAventineOffer);
              const townReduction = calculateReduction(roundTownOffer);
              
              return (
                <div 
                  key={roundData.round} 
                  className="border border-gray-200 rounded p-1.5 text-xs bg-gray-50"
                >
                  <div className="font-semibold mb-0.5 text-gray-600">
                    Round {roundData.round}
                  </div>
                  <div className="space-y-0.5">
                    {roundAventineOffer && (
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="font-medium text-green-700 text-[10px] min-w-[50px]">Ave:</span>
                        <span className="text-gray-800 text-[10px] font-bold">{aventineOfferValue || 'N/A'}</span>
                        {aventineReduction && (
                          <span className={`text-[10px] font-medium ${aventineReduction.isNegative ? 'text-red-600' : 'text-green-600'}`}>
                            {aventineReduction.display}
                          </span>
                        )}
                        <button
                          onClick={() => toggleAV(roundData.round, 'Aventine')}
                          className="flex items-center flex-shrink-0"
                          title="Toggle MV/AV"
                        >
                          <AutorenewIcon sx={{ fontSize: 12 }} className="text-gray-500" />
                        </button>
                        {aventineComment && (
                          <span className="text-gray-500 text-[10px] italic flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap" title={aventineComment}>
                            Note: {aventineComment}
                          </span>
                        )}
                      </div>
                    )}
                    {roundTownOffer && (
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="font-medium text-blue-700 text-[10px] min-w-[50px]">Town:</span>
                        <span className="text-gray-800 text-[10px] font-bold">{townOfferValue || 'N/A'}</span>
                        {townReduction && (
                          <span className={`text-[10px] font-medium ${townReduction.isNegative ? 'text-red-600' : 'text-green-600'}`}>
                            {townReduction.display}
                          </span>
                        )}
                        <button
                          onClick={() => toggleAV(roundData.round, 'Town')}
                          className="flex items-center flex-shrink-0"
                          title="Toggle MV/AV"
                        >
                          <AutorenewIcon sx={{ fontSize: 12 }} className="text-gray-500" />
                        </button>
                        {townComment && (
                          <span className="text-gray-500 text-[10px] italic flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap" title={townComment}>
                            Note: {townComment}
                          </span>
                        )}
                      </div>
                    )}
                    {!roundAventineOffer && !roundTownOffer && (
                      <div className="text-gray-400 italic text-[10px]">No offers</div>
                    )}
                  </div>
                </div>
              );
            }
            
            // Full view for current round with editable note
            const showAventineAV = showAV[`${roundData.round}-Aventine`];
            const showTownAV = showAV[`${roundData.round}-Town`];
            const aventineOfferValue = formatOffer(roundAventineOffer, showAventineAV);
            const townOfferValue = formatOffer(roundTownOffer, showTownAV);
            const aventineReduction = calculateReduction(roundAventineOffer);
            const townReduction = calculateReduction(roundTownOffer);
            
            return (
              <div 
                key={roundData.round} 
                className="border border-blue-500 rounded p-2 text-xs bg-blue-50"
              >
                <div className="font-semibold mb-1 text-gray-700">
                  Round {roundData.round} (Current)
                </div>
                <div className="space-y-0.5">
                  {roundAventineOffer && (
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-medium text-green-700 text-[10px] min-w-[50px]">Ave:</span>
                      <span className="text-gray-800 text-[10px] font-bold">{aventineOfferValue || 'N/A'}</span>
                      {aventineReduction && (
                        <span className={`text-[10px] font-medium ${aventineReduction.isNegative ? 'text-red-600' : 'text-green-600'}`}>
                          {aventineReduction.display}
                        </span>
                      )}
                      <button
                        onClick={() => toggleAV(roundData.round, 'Aventine')}
                        className="flex items-center"
                        title="Toggle MV/AV"
                      >
                        <AutorenewIcon sx={{ fontSize: 12 }} className="text-gray-500" />
                      </button>
                      <TextField
                        hiddenLabel
                        placeholder="Add note..."
                        autoComplete="off"
                        value={aventineNote}
                        onChange={(e) => handleNoteChange(e.target.value)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          sx: {
                            padding: 0,
                            '& .MuiInputBase-input': {
                              padding: '2px 4px',
                              fontSize: '10px',
                              height: '16px',
                              minHeight: '16px',
                            },
                          },
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '16px',
                            minHeight: '16px',
                            '& fieldset': {
                              border: '1px solid #d1d5db',
                            },
                            '&:hover fieldset': {
                              border: '1px solid #9ca3af',
                            },
                            '&.Mui-focused fieldset': {
                              border: '1px solid #3b82f6',
                            },
                          },
                          flex: 1,
                          minWidth: '120px',
                        }}
                      />
                      {isUpdating && (
                        <span className="text-[10px] text-gray-500">Saving...</span>
                      )}
                    </div>
                  )}
                  {roundTownOffer && (
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-medium text-blue-700 text-[10px] min-w-[50px]">Town:</span>
                      <span className="text-gray-800 text-[10px] font-bold">{townOfferValue || 'N/A'}</span>
                      {townReduction && (
                        <span className={`text-[10px] font-medium ${townReduction.isNegative ? 'text-red-600' : 'text-green-600'}`}>
                          {townReduction.display}
                        </span>
                      )}
                      <button
                        onClick={() => toggleAV(roundData.round, 'Town')}
                        className="flex items-center"
                        title="Toggle MV/AV"
                      >
                        <AutorenewIcon sx={{ fontSize: 12 }} className="text-gray-500" />
                      </button>
                      {roundTownOffer.Comments && roundTownOffer.Comments.trim() && (
                        <span className="text-gray-500 text-[10px] italic ml-1" title={roundTownOffer.Comments}>
                          Note: {getCommentPreview(roundTownOffer.Comments)}
                        </span>
                      )}
                    </div>
                  )}
                  {!roundAventineOffer && !roundTownOffer && (
                    <div className="text-gray-400 italic text-[10px]">No offers</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-xs text-gray-400 mb-2 italic">No negotiation rounds available</div>
      )}
    </div>
  );
};

// Property Record Card Component
const PropertyRecordCard = ({ 
    selectedProperty, 
    selectedProperties, 
    currentPropertyIndex, 
    onClose, 
    onNext, 
    onPrevious,
    handleProcessDBUpdate,
    handleCompViewClose,
    selectedComps,
    handleCompSelect,
    caseObject,
    onFixLatLong,
    map,
    isSaleRun = true
  }) => {
    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const comp = useMemo(() => parseInt(queryParams.get('comp')) - 1 || 0, [queryParams]);
    const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
    const setNegotiationObj = usePersistedDataStore((state) => state.setNegotiationObj);
    const muniCode = getNegotiationObj?.MuniCode;
    const [rarHistory, setRarHistory] = useState([]);
    const [rarError, setRarError] = useState(null);

    const rarHistoryTooltipSlotProps = useMemo(
      () => ({
        tooltip: {
          sx: (t) => ({
            bgcolor: t.palette.grey[900],
            color: t.palette.common.white,
            maxWidth: 320,
            fontSize: 13,
            lineHeight: 1.45,
            boxSizing: 'border-box',
            '& .MuiTooltip-arrow': {
              color: t.palette.grey[900],
            },
          }),
        },
      }),
      []
    );
  
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [activeTab, setActiveTab] = useState(selectedProperty?.isSubject ? 0 : 0);
    const [isPhotoPopupOpen, setIsPhotoPopupOpen] = useState(false);
    const getCaseNotes = usePersistedDataStore((state) => state.caseNotes);
    const setPersistedCaseNotes = usePersistedDataStore((state) => state.setCaseNotes);
    const { user } = useUser();
    const caseNotesResolved = useMemo(() => {
      const fromStore = getCaseNotes?.[comp];
      if (fromStore !== undefined) return fromStore ?? '';
      const resolvedCase = getNegotiationObj?.cases?.[comp] || caseObject;
      return resolvedCase?.CaseNotes ?? '';
    }, [getCaseNotes, comp, getNegotiationObj?.cases, caseObject]);
    const operatorMemos = useMemo(() => {
      const resolvedCase = getNegotiationObj?.cases?.[comp] || caseObject;
      const memos = resolvedCase?.Memos;
      if (!Array.isArray(memos)) return [];
      return memos.filter((entry) => entry?.memo && String(entry.memo).trim());
    }, [getNegotiationObj?.cases, comp, caseObject]);
    const sortedOperatorMemos = useMemo(
      () => [...operatorMemos].sort((a, b) => new Date(a.date) - new Date(b.date)),
      [operatorMemos]
    );
    const hasOperatorMemos = operatorMemos.length > 0;
    const homeownerNotes = useMemo(() => {
      const resolvedCase = getNegotiationObj?.cases?.[comp] || caseObject;
      const notes = resolvedCase?.HomeownerNotes;
      return notes != null ? String(notes) : '';
    }, [getNegotiationObj?.cases, comp, caseObject]);
    const hasHomeownerNotes = homeownerNotes.trim().length > 0;
    const caseNotesInputRef = useRef(null);
    const debounceTimerRef = useRef(null);
    const selectedCompsRef = useRef(selectedComps);
    useEffect(() => {
      selectedCompsRef.current = selectedComps;
    }, [selectedComps]);
    const [isSavingCaseNotes, setIsSavingCaseNotes] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [isFlaggingBadSale, setIsFlaggingBadSale] = useState(false);
    const [isStreetViewOpen, setIsStreetViewOpen] = useState(false);
    const [hasStreetView, setHasStreetView] = useState(false);
    const [isLoadingStreetView, setIsLoadingStreetView] = useState(false);
    const [streetViewData, setStreetViewData] = useState(null);
    const [showMemosScrollIndicator, setShowMemosScrollIndicator] = useState(false);
    const [isMemosAtBottom, setIsMemosAtBottom] = useState(false);
    const memosRef = useRef(null);
    const [activeNotesTab, setActiveNotesTab] = useState('caseNotes');
    const [isCaseInfoExpanded, setIsCaseInfoExpanded] = useState(false);
    
    // Photo upload states
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [salesWebOpen, setSalesWebOpen] = useState(false);
    const [salesWebDownloading, setSalesWebDownloading] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [photoDetailLoading, setPhotoDetailLoading] = useState(false);

    const mlsPhotos = useMemo(() => parseMLSPhotoFolder(selectedProperty?.MLSPhotoFolder), [selectedProperty?.MLSPhotoFolder]);
    const salesWebScreenshot = (() => {
      const raw = selectedProperty?.SalesWebScreenshot;
      if (raw == null) return '';
      const url = String(raw).trim();
      return !url || url === 'null' || url === 'undefined' ? '' : url;
    })();
    const subjectTabOffset = selectedProperty?.isSubject ? 1 : 0;
    const downloadSalesWebPdf = useCallback(async () => {
      if (!salesWebScreenshot || salesWebDownloading) return;
      setSalesWebDownloading(true);
      try {
        const { data } = await axiosInstance.post(
          '/salesweb_screenshot',
          { url: salesWebScreenshot },
          { responseType: 'arraybuffer' }
        );
        const { PDFDocument } = await import('pdf-lib');
        const pdf = await PDFDocument.create();
        const bytes = new Uint8Array(data);
        const isPng = bytes[0] === 0x89 && bytes[1] === 0x50;
        const image = isPng ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes);
        const scale = Math.min(1, 14400 / image.width, 14400 / image.height);
        const width = image.width * scale;
        const height = image.height * scale;
        const page = pdf.addPage([width, height]);
        page.drawImage(image, { x: 0, y: 0, width, height });
        const pdfBytes = await pdf.save();
        const objectUrl = URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }));
        const link = document.createElement('a');
        const base = (selectedProperty?.address || selectedProperty?.parcel_id || 'sales-web')
          .replace(/[^\w.-]+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '') || 'sales-web';
        link.href = objectUrl;
        link.download = `${base}-sales-web.pdf`;
        link.click();
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        logger.error('Sales Web PDF download failed', error);
        if (!error?.response) toast.error('Could not build the Sales Web PDF');
      } finally {
        setSalesWebDownloading(false);
      }
    }, [salesWebScreenshot, salesWebDownloading, selectedProperty?.address, selectedProperty?.parcel_id]);
    const hasMlsPhotos = mlsPhotos.length > 0;
    const photoCarousel = usePhotoCarousel(mlsPhotos);
    const { actualUrl: actualPhotoUrl, hasPhoto } = usePhotoImage(selectedProperty?.parcel_id);
    const { uploading: isUploadingPhoto, uploadNewPhoto, setPrimaryPhoto, removePhoto } = usePhotoActions(selectedProperty?.parcel_id);
    const livePhotoUrl = hasPhoto ? actualPhotoUrl : null;
    const photoUrl = photoCarousel.index === -1 ? livePhotoUrl : mlsPhotos[photoCarousel.index];

    useEffect(() => {
      photoCarousel.reset();
      setSalesWebOpen(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProperty?.parcel_id]);

    const closePhotoPopup = useCallback(() => {
      setIsPhotoPopupOpen(false);
      photoCarousel.reset();
    }, [photoCarousel]);

    const openPhotoPopup = useCallback(() => {
      setPhotoDetailLoading(!!photoUrl);
      setIsPhotoPopupOpen(true);
    }, [photoUrl]);

    // Helper function to determine case status
    const getCaseStatus = useCallback(() => {
      if (!caseObject) return 'Unknown';
      
      const { BARDeterminationAction, SCARFiled, SCARDeterminationAction } = caseObject;
      
      // If SCAR not filed
      if (SCARFiled === 0) {
        if (BARDeterminationAction === 'BD' || BARDeterminationAction === 'BW') {
          return 'Lost';
        }
        if (BARDeterminationAction === 'S' || BARDeterminationAction === 'ST') {
          return 'Won (No SCAR)';
        }
      }
      
      // If SCAR filed
      if (SCARFiled === 1) {
        if (SCARDeterminationAction === 'S' || SCARDeterminationAction === 'ST') {
          return 'Won';
        }
        if (SCARDeterminationAction === 'SD' || SCARDeterminationAction === 'W') {
          return 'Lost';
        }
      }
      
      return 'Pending';
    }, [caseObject]);
  
    // Helper function to determine if case is settled
    const isSettled = useCallback(() => {
      if (!caseObject) return false;
      return caseObject.SCARDeterminationAction === 'S' || caseObject.BARDeterminationAction === 'S';
    }, [caseObject]);
  
    const handleCaseNotesChange = useCallback(async (newValue) => {
      
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
  
      // Set new timer for debounced save
      debounceTimerRef.current = setTimeout(async () => {
        if (isSavingCaseNotes) return;
        
        setIsSavingCaseNotes(true);
        
        try {
          const storeState = usePersistedDataStore.getState();
          const freshCaseNotes = storeState.caseNotes;
          const freshNegotiationObj = storeState.negotiationObj;
          
          const normalizedCaseNotes = normalizeCaseNotes(newValue);
          const updatedCaseNotes = [...freshCaseNotes];
          updatedCaseNotes[comp] = normalizedCaseNotes;
          setPersistedCaseNotes(updatedCaseNotes);
          
          const currentCase = freshNegotiationObj?.cases?.[comp];
          if (!currentCase) {
            setIsSavingCaseNotes(false);
            return;
          }

          const subjectParcelId = currentCase.parcel_id;
          const liveSelectedComps = selectedCompsRef.current;
          const compsToSave = Array.isArray(liveSelectedComps)
            ? liveSelectedComps.filter((pid) => pid && pid !== subjectParcelId)
            : (currentCase.Comps ? Object.values(currentCase.Comps).filter(Boolean) : []);
          
          const subjectRow = {
            parcel_id: currentCase.parcel_id,
            PropertyInfo: { Address: currentCase.Address },
            Address: currentCase.Address
          };
          
          const saveData = createSaveObject({
            selectedComps: compsToSave,
            caseIndex: comp,
            subjectRow
          });
          
          const { compsObject, selectedIndexRegular, selectedIndexOptimized, address, repId } = saveData;
          const resolvedCaseNotes = resolveCaseNotesForCase(comp, normalizedCaseNotes, currentCase);
          
          await batchSaveComps({
            negotiationObj: freshNegotiationObj,
            cases: [currentCase],
            user,
            options: {
              manualReviewFlag: 1,
              manualOverride: 1,
              manualCompSave: true,
              saveData: {
                0: {
                  compsObject,
                  selectedIndexRegular,
                  selectedIndexOptimized,
                  address,
                  repId,
                  caseNotes: resolvedCaseNotes,
                }
              }
            }
          });

          const updatedCases = [...freshNegotiationObj.cases];
          updatedCases[comp] = {
            ...currentCase,
            Comps: compsObject,
            CaseNotes: resolvedCaseNotes,
            SelectedIndexRegular: selectedIndexRegular,
            SelectedIndexOptimized: selectedIndexOptimized,
            RunDate: new Date().toUTCString(),
            ManualReview: 1,
            ReviewedBy: user?.userId,
          };
          setNegotiationObj({ ...freshNegotiationObj, cases: updatedCases });
          
          logger.log('✅ Auto-saved caseNotes');
        } catch (error) {
          logger.error('❌ Error auto-saving caseNotes:', error);
        } finally {
          setIsSavingCaseNotes(false);
        }
      }, 1000); // 1 second debounce (same as NegotiationsNotesInput)
    }, [comp, setPersistedCaseNotes, setNegotiationObj, isSavingCaseNotes, user]);
  
    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    // Focus notes tab when requested from toolbar
    useEffect(() => {
      if (!selectedProperty?.isSubject) return;

      if (selectedProperty?.focusInternalCaseNotes) {
        setActiveTab(0);
        setActiveNotesTab('internalMemo');
        setTimeout(() => {
          const textarea = document.querySelector('textarea[placeholder="Type any internal case notes here"]');
          if (textarea) {
            textarea.focus();
            const length = textarea.value.length;
            textarea.setSelectionRange(length, length);
          }
        }, 100);
      } else if (selectedProperty?.focusOperatorMemos && hasOperatorMemos) {
        setActiveTab(0);
        setActiveNotesTab('operatorMemos');
      } else if (selectedProperty?.focusHomeownerNotes && hasHomeownerNotes) {
        setActiveTab(0);
        setActiveNotesTab('homeownerNotes');
      }
    }, [selectedProperty?.focusInternalCaseNotes, selectedProperty?.focusOperatorMemos, selectedProperty?.focusHomeownerNotes, selectedProperty?.isSubject, hasOperatorMemos, hasHomeownerNotes]);
  
    // Add effect to check Street View availability
    useEffect(() => {
      const checkStreetViewAvailability = async () => {
        const lat = selectedProperty?.latitude;
        const lng = selectedProperty?.longitude;
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

        if (!lat || !lng || !apiKey) {
          logger.warn('[StreetView] Missing lat/lng or API key');
          setHasStreetView(false);
          return;
        }

        // Check cache first
        const cachedData = streetViewCache.getAvailability(lat, lng);
        if (cachedData !== undefined) {
          logger.log('[StreetView] Using cached data:', cachedData);
          setHasStreetView(cachedData.status === "OK");
          setStreetViewData(cachedData);
          return;
        }

        setIsLoadingStreetView(true);
        try {
          // Try with radius=50 first, then fallback to radius=100 if needed
          const checkWithRadius = async (radius) => {
            const url = `https://maps.googleapis.com/maps/api/streetview/metadata?location=${lat},${lng}&radius=${radius}&key=${apiKey}`;
            
            const res = await fetch(url);
            const data = await res.json();
            return data;
          };

          // Try radius=50 first
          let data = await checkWithRadius(50);
          
          // If no result, try radius=100
          if (data.status !== "OK") {
            data = await checkWithRadius(100);
          }

          // Cache the result regardless of success
          streetViewCache.setAvailability(lat, lng, data);
          
          if (data.status === "OK") {
            setHasStreetView(true);
            setStreetViewData(data);
          } else {
            logger.warn('[StreetView] No imagery available:', data.status);
            setHasStreetView(false);
            setStreetViewData(null);
          }
        } catch (err) {
          logger.error('[StreetView] Metadata fetch error:', err);
          setHasStreetView(false);
          setStreetViewData(null);
        } finally {
          setIsLoadingStreetView(false);
        }
      };

      checkStreetViewAvailability();
    }, [selectedProperty]);
  
    // Fetch RAR history when tab is selected and muniCode is available
    useEffect(() => {
      if (activeTab === subjectTabOffset + 2 && muniCode) {
        let cancelled = false;
        setRarError(null);
        fetchRarHistory(muniCode)
          .then((data) => { if (!cancelled) setRarHistory(data); })
          .catch((err) => {
            if (!cancelled) setRarError(err?.response?.data?.error || err.message || 'Failed to load RAR history');
          });
        return () => { cancelled = true; };
      }
    }, [activeTab, muniCode, subjectTabOffset]);
  
    const handleMenuOpen = (event) => {
      setMenuAnchorEl(event.currentTarget);
    };
  
    const handleMenuClose = () => {
      setMenuAnchorEl(null);
    };
  
  
  
    const handleTabChange = (event, newValue) => {
      setActiveTab(newValue);
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
    };

    const formatMemoDate = (dateStr) => {
      if (!dateStr) return '';
      const parsed = new Date(String(dateStr).replace(' ', 'T'));
      if (Number.isNaN(parsed.getTime())) return String(dateStr).split(/[ T]/)[0];
      return parsed.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
      });
    };
  
    const handleFlagBadSaleClick = () => {
      handleMenuClose();
      setConfirmDialogOpen(true);
    };
  
    const handleConfirmFlagBadSale = async ({ SaleIssue, Memo }) => {
      setIsFlaggingBadSale(true);
      try {
        const result = await flagSingleBadSale({
          TransactionID: selectedProperty.TransactionID,
          SaleIssue,
          Memo,
          UserID: user?.userId,
        });

        if (result.success) {
          setConfirmDialogOpen(false);
          handleClose();
          handleProcessDBUpdate(selectedProperty.parcel_id);
          handleCompViewClose();
        }
      } catch (error) {
        logger.error('Error flagging bad sale:', error);
      } finally {
        setIsFlaggingBadSale(false);
      }
    };
  
    const handleCancelFlagBadSale = () => {
      setConfirmDialogOpen(false);
    };
  
    // Photo upload handler — shared by the dialog's file input and both drop zones below
    const handlePhotoUpload = useCallback(async (file) => {
      setUploadDialogOpen(false);
      await uploadNewPhoto(file);
    }, [uploadNewPhoto]);

    const handleSetPrimaryPhoto = useCallback(async () => {
      const sourceUrl = mlsPhotos[photoCarousel.index];
      if (!sourceUrl) return;
      const newUrl = await setPrimaryPhoto(sourceUrl);
      if (newUrl) photoCarousel.reset();
    }, [mlsPhotos, photoCarousel, setPrimaryPhoto]);

    // Photo delete handler
    const handlePhotoDelete = useCallback(async () => {
      await removePhoto();
      setDeleteConfirmOpen(false);
    }, [removePhoto]);

    const { dragActive, dragHandlers } = useDragAndDrop(handlePhotoUpload);

    const isLivePhoto = photoCarousel.index === -1;

    // Same "upload a new one" vs "promote this MLS photo" action PhotoCell's dialog uses.
    const handlePrimaryPhotoAction = useCallback(() => {
      if (isLivePhoto) {
        setUploadDialogOpen(true);
        return;
      }
      handleSetPrimaryPhoto();
    }, [isLivePhoto, handleSetPrimaryPhoto]);

    const photoDetailEmptyState = (
      <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center text-center rounded">
        <svg className="w-24 h-24 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="text-gray-500 mb-4 text-lg">No Photo Available</span>
        <div className="flex flex-col items-center gap-2">
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            size="large"
          >
            Upload Photo
          </Button>
          <span className="text-sm text-gray-400">or drag and drop an image here</span>
        </div>
      </div>
    );
  
    // Helper function to get user name by ID
    const getUserNameById = useCallback((userId) => {
      if (!userId) return 'Unknown';
      
      // Find the user in userObject by matching userId
      const userEntry = Object.values(userObject).find(u => u.userId === userId);
      return userEntry ? userEntry.userName : 'Unknown';
    }, []);
  
    const handleClose = () => {
      if (map) {
        // Reset all styling except selected comps layer
        resetAllStyling(map, true);
      }
      onClose();
    };
  
    // Add scroll detection for memos
    useEffect(() => {
      const memosContainer = memosRef.current;
      if (memosContainer && activeNotesTab === 'operatorMemos') {
        const isScrollable = memosContainer.scrollHeight > memosContainer.clientHeight;
        setShowMemosScrollIndicator(isScrollable);

        const handleScroll = () => {
          const isBottom = Math.abs(memosContainer.scrollHeight - memosContainer.scrollTop - memosContainer.clientHeight) < 1;
          setIsMemosAtBottom(isBottom);
        };

        memosContainer.addEventListener('scroll', handleScroll);
        handleScroll();
        return () => memosContainer.removeEventListener('scroll', handleScroll);
      }

      setShowMemosScrollIndicator(false);
      setIsMemosAtBottom(false);
    }, [sortedOperatorMemos, activeNotesTab]);

  
    // If Street View is open, show only the Street View
    if (isStreetViewOpen) {
      return (
        <StreetView 
          onClose={() => setIsStreetViewOpen(false)}
          lat={selectedProperty?.latitude}
          lng={selectedProperty?.longitude}
          panoId={streetViewData?.pano_id}
        />
      );
    }
  
    return (
      <div 
        className={`h-full w-full bg-white shadow-lg overflow-hidden ${
          dragActive ? 'outline-2 outline-blue-500 outline bg-blue-50' : ''
        }`}
        {...dragHandlers}
      >
        <div className={`h-full flex flex-col ${selectedProperty?.isSubject && isSettled() ? 'bg-green-50' : ''}`}>
          {dragActive && (
            <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center z-50">
              <div className="text-blue-600 text-lg font-medium bg-white px-4 py-2 rounded shadow-lg">
                Drop photo to upload
              </div>
            </div>
          )}
          {/* Header with Address and Photo */}
          <div className="flex items-start justify-between p-3 pb-1 pt-0">
            <div className="flex-1">
              <div className="flex items-center space-x-1">
                <h2 
                  className="text-lg font-bold underline hover:text-blue-500 cursor-pointer" 
                  onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(`${selectedProperty.address}, ${selectedProperty.town}, ${STATE} ${selectedProperty.zip}`)}`, '_blank')}
                >
                  {selectedProperty.address}
                </h2>
                <CopyableIdCell 
                  value={`${selectedProperty.address}, ${selectedProperty.town}, ${STATE} ${selectedProperty.zip}`}
                  label="Address"
                  size="medium"
                  showValue={false}
                />
                <Tooltip title="Ask AI for Property Inventory">
                  <IconButton 
                    size="small" 
                    onClick={() => window.open(`https://chat.openai.com/?model=gpt-4&prompt=${encodeURIComponent(`For the property
${selectedProperty.address}, ${selectedProperty.town}, ${STATE} ${selectedProperty.zip}
provide the following information
What is the latest sale price and sale date for this property?
Gross Living Area: What is the property's gross living area?
Acreage: What is the property's acreage?
Please provide a photo of this home.
What is the Style of this home?
What is the year built for this home?
Was this property recently rennovated if so in what year?
How many full Baths does this home have?
How many half Baths does this home have?
How many kitchens Baths does this home have?
How many bedrooms does this home have?
How many Garages does this home have?
Does this home have an in-ground swimming pool?
Does this home have a Basement? If so is it: (Full finished/ Partial Finished / Unfinished) and estimate the square 13) What is the conditon of this home?
What is the grade of this home?
What is the measurement in automobile traffic in from of this property? Calculate if needed. Is this measurement low, medium or high?
What is a range value estimate of this home?`)}`, '_blank')}
                    sx={{ padding: '1px' }}
                  >
                    <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </div>
              <div className="flex flex-col mt-0 text-xs text-gray-600">
              {selectedProperty.town && selectedProperty.zip ? 
                  `${selectedProperty.town}, ${selectedProperty.zip}` : 
                  selectedProperty.town || selectedProperty.zip || ''
                }
                {selectedProperty.SDName && (
                  <div className="flex items-center">
                    <span>SD: {selectedProperty.SDName} ({selectedProperty.SDCode})</span>
                  </div>
                )}
                <div className="flex items-center">
                  <span>PID: </span>
                  <CopyableIdCell 
                    value={selectedProperty.parcel_id}
                    label="Parcel ID"
                    size="small"
                  />
                </div>
                <div className="flex items-center">
                  <span>
                    {selectedProperty.isSubject ? (
                      <span className="font-bold">Subject Property</span>
                    ) : selectedProperty.id_number ? (
                      <div className="flex items-center gap-2">
                        <span>Algorithm #{selectedProperty.id_number}</span>
                        {selectedProperty.selectedRowNumber > 0 && (
                          <span className="ml-2 text-blue-600">(Selected comp #{selectedProperty.selectedRowNumber})</span>
                        )}
                        <TooltipCheckbox 
                          checked={selectedComps?.includes(selectedProperty.parcel_id)}
                          onClick={() => handleCompSelect(selectedProperty.parcel_id)}
                        />
                      </div>
                    ) : null}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-start">
              <div className="absolute right-24 top-0">
                {/* Property Photo */}
                <div 
                  className="w-32 h-[107px] rounded-t border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity relative"
                  onClick={openPhotoPopup}
                >
                  {isUploadingPhoto ? (
                    <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center text-center">
                      <CircularProgress size={24} sx={{ color: '#1976d2', mb: 1 }} />
                      <span className="text-xs text-gray-500">Uploading...</span>
                    </div>
                  ) : photoUrl ? (
                    <>
                      <img
                        src={photoUrl}
                        alt="Property"
                        className="w-full h-full object-cover"
                      />
                      {hasMlsPhotos && (
                        <>
                          <PhotoOverlayArrows onPrev={photoCarousel.goPrev} onNext={photoCarousel.goNext} />
                          <PhotoCountBadge current={photoCarousel.displayNumber} total={photoCarousel.total} />
                        </>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex flex-col items-center justify-center text-center">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-xs text-gray-500 px-1 mb-4">No Photo Available</span>
                      <span className="text-xs text-blue-500">Drop photo here</span>
                    </div>
                  )}
                  {/* Street View — small icon badge, tooltip explains why it's disabled */}
                  <div className="absolute top-1 right-1 z-10" onClick={(e) => e.stopPropagation()}>
                    {isLoadingStreetView ? (
                      <Tooltip title="Checking Street View availability...">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/90 shadow">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500"></div>
                        </span>
                      </Tooltip>
                    ) : hasStreetView ? (
                      <Tooltip title="Street View">
                        <IconButton
                          size="small"
                          onClick={() => setIsStreetViewOpen(true)}
                          sx={{ width: 24, height: 24, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' } }}
                        >
                          <VisibilityIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Street View not available for this property's lat/long">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/90 shadow text-gray-400">
                          <VisibilityOffIcon sx={{ fontSize: 14 }} />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <IconButton onClick={handleMenuOpen} size="small">
                  <MoreVertIcon />
                </IconButton>
                <IconButton onClick={handleClose} size="small">
                  <CloseIcon style={{ color: 'red' }} />
                </IconButton>
              </div>
            </div>
          </div>
  
          <Menu 
            anchorEl={menuAnchorEl} 
            open={Boolean(menuAnchorEl)} 
            onClose={handleMenuClose}
            container={() => document.body}
            style={{ zIndex: 10001 }}
          >
            <MenuItem onClick={handleFlagBadSaleClick} disabled={!isSaleRun}>Flag bad sale</MenuItem>
            <MenuItem onClick={() => {
              handleMenuClose();
              onFixLatLong(selectedProperty);
            }}>Fix Lat/Long</MenuItem>
          </Menu>
  
          <StableMuiDialog
            open={salesWebOpen && Boolean(salesWebScreenshot)}
            onClose={() => setSalesWebOpen(false)}
            maxWidth="lg"
            fullWidth
            container={() => document.body}
            style={{ zIndex: 10001 }}
            PaperProps={{
              sx: {
                height: '92vh',
                maxHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            <AppBar position="static" sx={{ backgroundColor: 'background.paper', color: 'text.primary', boxShadow: 1, flexShrink: 0 }}>
              <Toolbar sx={{ minHeight: '48px !important', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                  <Typography variant="h6" sx={{ lineHeight: 1.2, flexShrink: 0 }}>Sales Web</Typography>
                  {selectedProperty?.address && (
                    <Typography sx={{ fontSize: 13, lineHeight: 1.25, color: 'text.secondary' }} noWrap>
                      {selectedProperty.address}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 13, lineHeight: 1.25, flexShrink: 0 }} noWrap>
                    {selectedProperty?.salePrice && !isNaN(selectedProperty.salePrice) ? (
                      <>
                        ${Number(selectedProperty.salePrice).toLocaleString()}
                        {selectedProperty.saleDate ? ` on ${formatDate(selectedProperty.saleDate)}` : ''}
                      </>
                    ) : (
                      'No sale'
                    )}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    disabled={salesWebDownloading}
                    onClick={downloadSalesWebPdf}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      backgroundColor: '#4b5563',
                      '&:hover': { backgroundColor: '#374151' },
                      '&.Mui-disabled': { backgroundColor: '#f3f4f6 !important', color: '#9ca3af' },
                    }}
                  >
                    {salesWebDownloading ? 'Downloading…' : 'Download'}
                  </Button>
                  <IconButton edge="end" color="inherit" onClick={() => setSalesWebOpen(false)} aria-label="close">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Toolbar>
            </AppBar>
            <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', bgcolor: '#525659' }}>
              <Box sx={{ flex: 1, minHeight: 0, m: '12px', bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.45)', overflow: 'hidden' }}>
                <iframe
                  title="Sales Web"
                  srcDoc={`<!DOCTYPE html><html><head><style>html,body{margin:0;background:#fff}img{width:100%;height:auto;display:block}</style></head><body><img src="${salesWebScreenshot.replace(/"/g, '&quot;')}" alt="Sales Web" /></body></html>`}
                  onLoad={(event) => {
                    const frame = event.currentTarget;
                    const doc = frame.contentDocument;
                    const win = frame.contentWindow;
                    if (!doc || !win) return;
                    const scrollDown = () => {
                      const maxScroll = Math.max(0, doc.documentElement.scrollHeight - win.innerHeight);
                      win.scrollTo(0, maxScroll * 0.6);
                    };
                    const img = doc.querySelector('img');
                    if (img && !img.complete) img.addEventListener('load', () => requestAnimationFrame(scrollDown), { once: true });
                    else requestAnimationFrame(scrollDown);
                  }}
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block', background: '#fff' }}
                />
              </Box>
            </DialogContent>
          </StableMuiDialog>

          <FlagBadSaleDialog
            open={confirmDialogOpen}
            onClose={handleCancelFlagBadSale}
            onConfirm={handleConfirmFlagBadSale}
            submitting={isFlaggingBadSale}
            subtitle={selectedProperty?.address || 'This sale'}
            sales={[{ price: selectedProperty?.salePrice, date: selectedProperty?.saleDate }]}
          />
  
          {/* Content */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="flex flex-col h-full">
              <Tabs 
                className="border-b"
                value={activeTab} 
                onChange={handleTabChange} 
                size="small"
                sx={{ 
                  minHeight: '24px',
                  '& .MuiTab-root': {
                    minHeight: '24px',
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                    textTransform: 'none'
                  },
                  '& .MuiTabs-indicator': {
                    height: '2px'
                  }
                }}
              >
                {selectedProperty?.isSubject && <Tab label="Case Info" />}
                <Tab label="MLS" />
                <Tab label="Inventory" />
                {muniCode && <Tab label="RAR History" />}
              </Tabs>
  
              {/* Case Info Tab */}
              {selectedProperty?.isSubject && activeTab === 0 && (
                <div className="p-2">
                  {/* Debug: Log case object to console */}
                  {/* {logger.log('Case Object:', caseObject)} */}
                                    {/* Case Details - Row 1 */}
                  <div className="grid grid-cols-4 gap-2 mb-1.5">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Client</p>
                      <p className="text-sm truncate">{caseObject?.ClientName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Index #</p>
                      <div className="flex items-center">
                        <CopyableIdCell 
                          value={caseObject?.SCARIndexNumber}
                          label="SCAR Index Number"
                          size="small"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Reviewed By</p>
                      <p className="text-sm truncate">{getUserNameById(caseObject?.ReviewedBy) || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Run Date</p>
                      <p className="text-sm">{formatDateWithoutTimezone(caseObject?.RunDate)}</p>
                    </div>
                  </div>

                 {/* Case Status & Key Values - Row 2 */}
                  <div className="grid grid-cols-4 gap-2 mb-1.5">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Status</p>
                      <p className={`text-sm font-semibold ${
                        getCaseStatus().includes('Won') ? 'text-green-600' : 
                        getCaseStatus() === 'Lost' ? 'text-red-600' : 
                        'text-gray-600'
                      }`}>
                        {getCaseStatus()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">IFMV</p>
                      <p className="text-sm font-medium">
                        {selectedProperty?.ifmv != null && !isNaN(selectedProperty.ifmv)
                          ? `$${Number(selectedProperty.ifmv).toLocaleString()}`
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Taxes</p>
                      <p className="text-sm font-medium">
                        {selectedProperty?.taxes != null && !isNaN(selectedProperty.taxes)
                          ? `$${Number(selectedProperty.taxes).toLocaleString()}`
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">SCAR Filed</p>
                      <p className="text-sm font-medium">{caseObject?.SCARFiled === 1 ? 'Yes' : 'No'}</p>
                    </div>
                  </div>

                  {/* Expand Toggle - Show when collapsed */}
                  {!isCaseInfoExpanded && (
                    <div className="flex justify-center mb-1.5">
                      <button
                        onClick={() => setIsCaseInfoExpanded(!isCaseInfoExpanded)}
                        className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                        <span>See more</span>
                      </button>
                    </div>
                  )}

                 {/* BAR Information - Row 3 */}
                  {isCaseInfoExpanded && (
                    <div className="grid grid-cols-4 gap-2 mb-1.5">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">BAR Action</p>
                        <p className="text-sm font-medium">
                          {caseObject?.BARDeterminationAction ? 
                            (caseObject.BARDeterminationAction === 'SD' || caseObject.BARDeterminationAction === 'BD' ? 'Denied' :
                             caseObject.BARDeterminationAction === 'W' || caseObject.BARDeterminationAction === 'BW' ? 'Withdrawn' :
                             caseObject.BARDeterminationAction === 'S' || caseObject.BARDeterminationAction === 'ST' || caseObject.BARDeterminationAction === 'BS' ? 'Settled' :
                             caseObject.BARDeterminationAction) : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">BAR Requested</p>
                        <p className="text-sm font-medium">
                          {caseObject?.BARRequestedValue && caseObject.BARRequestedValue > 0 ? 
                            `$${Number(caseObject.BARRequestedValue).toLocaleString()}` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">BAR Determined</p>
                        <p className="text-sm font-medium">
                          {caseObject?.BARDeterminationValue && caseObject.BARDeterminationValue > 0 ? 
                            `$${Number(caseObject.BARDeterminationValue).toLocaleString()}` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">BAR Date</p>
                        <p className="text-sm font-medium">{formatDateWithoutTimezone(caseObject?.BARDeterminationDate) || 'N/A'}</p>
                      </div>
                    </div>
                  )}

                  {/* SCAR Information - Row 4 */}
                  {isCaseInfoExpanded && caseObject?.SCARFiled === 1 && (
                    <div className="grid grid-cols-4 gap-2 mb-1.5">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">SCAR Action</p>
                        <p className="text-sm font-medium">
                          {caseObject?.SCARDeterminationAction ? 
                            (caseObject.SCARDeterminationAction === 'SD' || caseObject.SCARDeterminationAction === 'BD' ? 'Denied' :
                             caseObject.SCARDeterminationAction === 'W' || caseObject.SCARDeterminationAction === 'BW' ? 'Withdrawn' :
                             caseObject.SCARDeterminationAction === 'S' || caseObject.SCARDeterminationAction === 'ST' || caseObject.SCARDeterminationAction === 'BS' ? 'Settled' :
                             caseObject.SCARDeterminationAction) : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">SCAR Requested</p>
                        <p className="text-sm font-medium">
                          {caseObject?.SCARRequestedValue && caseObject.SCARRequestedValue > 0 ? 
                            `$${Number(caseObject.SCARRequestedValue).toLocaleString()}` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">SCAR Determined</p>
                        <p className="text-sm font-medium">
                          {caseObject?.SCARDeterminationValue && caseObject.SCARDeterminationValue > 0 ? 
                            `$${Number(caseObject.SCARDeterminationValue).toLocaleString()}` : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Settle Date</p>
                        <p className="text-sm font-medium">{formatDateWithoutTimezone(caseObject?.SCARSettleDate) || 'N/A'}</p>
                      </div>
                    </div>
                  )}

                  {/* Collapse Toggle - Show when expanded, at the bottom */}
                  {isCaseInfoExpanded && (
                    <div className="flex justify-center mb-1.5">
                      <button
                        onClick={() => setIsCaseInfoExpanded(!isCaseInfoExpanded)}
                        className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                        <span>See less</span>
                      </button>
                    </div>
                  )}
  
                  {/* Case Notes & Internal Memo Tabs */}
                  <div className="mt-0 flex flex-col flex-1 min-h-0">
                    <div className="flex border-b border-gray-200 mb-0 flex-shrink-0">
                      <button
                        onClick={() => setActiveNotesTab('caseNotes')}
                        className={`px-3 py-0 text-xs font-medium border-b-2 transition-colors ${
                          activeNotesTab === 'caseNotes' 
                            ? 'border-blue-500 text-blue-600' 
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Negotiation Info
                      </button>
                      <button
                        onClick={() => setActiveNotesTab('internalMemo')}
                        className={`px-3 py-0 text-xs font-medium border-b-2 transition-colors ${
                          activeNotesTab === 'internalMemo' 
                            ? 'border-blue-500 text-blue-600' 
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          Internal Memo
                          {caseNotesResolved && String(caseNotesResolved).trim() && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        disabled={!hasOperatorMemos}
                        onClick={() => hasOperatorMemos && setActiveNotesTab('operatorMemos')}
                        className={`px-3 py-0 text-xs font-medium border-b-2 transition-colors ${
                          !hasOperatorMemos
                            ? 'border-transparent text-gray-400 cursor-not-allowed opacity-50'
                            : activeNotesTab === 'operatorMemos'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          Operator Memos
                          {hasOperatorMemos && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </button>
                      <button
                        type="button"
                        disabled={!hasHomeownerNotes}
                        onClick={() => hasHomeownerNotes && setActiveNotesTab('homeownerNotes')}
                        className={`px-3 py-0 text-xs font-medium border-b-2 transition-colors ${
                          !hasHomeownerNotes
                            ? 'border-transparent text-gray-400 cursor-not-allowed opacity-50'
                            : activeNotesTab === 'homeownerNotes'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-1">
                          Homeowner Notes
                          {hasHomeownerNotes && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </button>
                    </div>
                    
                    {activeNotesTab === 'caseNotes' && (
                      <div className="bg-white relative overflow-hidden flex-1 min-h-0">
                        <NegotiationsNotesInput
                          caseObject={caseObject}
                          selectedProperty={selectedProperty}
                        />
                      </div>
                    )}
                    {activeNotesTab === 'internalMemo' && (
                      <div className="bg-white relative overflow-hidden flex-1 min-h-0">
                        <CaseNotesInput
                          ref={caseNotesInputRef}
                          initialValue={caseNotesResolved}
                          onValueChange={handleCaseNotesChange}
                          maxRows={2}
                        />
                        {isSavingCaseNotes && (
                          <div className="absolute bottom-0 right-0 text-xs text-gray-500 mr-2 mb-1">
                            Saving...
                          </div>
                        )}
                      </div>
                    )}
                    {activeNotesTab === 'operatorMemos' && (
                      <div className="bg-white relative overflow-hidden flex-1 min-h-0">
                        <div
                          ref={memosRef}
                          className="overflow-y-auto max-h-48 pr-1 space-y-2 py-1"
                        >
                          {sortedOperatorMemos.map((entry, index) => (
                            <div
                              key={`${entry.date}-${entry.user}-${index}`}
                              className="border border-gray-200 rounded p-2 text-xs bg-gray-50"
                            >
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-gray-500 mb-1">
                                <span>{formatMemoDate(entry.date)}</span>
                                {entry.user && <span>{entry.user}</span>}
                                {entry.type && (
                                  <span className="px-1 py-0.5 rounded bg-gray-200 text-gray-700">
                                    {entry.type}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                                {entry.memo}
                              </p>
                            </div>
                          ))}
                        </div>
                        {showMemosScrollIndicator && !isMemosAtBottom && (
                          <div className="absolute bottom-0 left-0 right-0 flex justify-center pointer-events-none">
                            <KeyboardArrowDownIcon fontSize="small" className="text-gray-400 animate-bounce" />
                          </div>
                        )}
                      </div>
                    )}
                    {activeNotesTab === 'homeownerNotes' && (
                      <div className="bg-white relative overflow-hidden flex-1 min-h-0">
                        <div className="overflow-y-auto max-h-48 py-1 px-1">
                          <p className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                            {homeownerNotes}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
  
              {/* MLS Tab */}
              {activeTab === subjectTabOffset && (
                <>
                  {/* Sale Info */}
                  <div className="px-3 py-2 border-b flex items-start justify-between gap-2">
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm">
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-1">Sale:</span>
                        {selectedProperty.salePrice && !isNaN(selectedProperty.salePrice) ? (
                          <>
                            <span>${Number(selectedProperty.salePrice).toLocaleString()}</span>
                            <span className="text-xs ml-1">on {formatDate(selectedProperty.saleDate)}</span>
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">None</span>
                        )}
                      </div>
                      {selectedProperty.adjustedPrice && !isNaN(selectedProperty.adjustedPrice) && (
                        <div className="flex items-center">
                          <span className="mx-2 text-gray-300">|</span>
                          <span className="text-gray-500 mr-1">{isSaleRun ? 'Adj Sale:' : 'Adj IFMV:'}</span>
                          <span className={selectedProperty.supportsReduction ? 'text-green-600' : 'text-red-600'}>
                            ${Math.round(selectedProperty.adjustedPrice).toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <span className="mx-2 text-gray-300">|</span>
                        <span className="text-gray-500 mr-1">MLS:</span>
                        <CopyableIdCell
                          value={selectedProperty.MLSNumber}
                          label="MLS Number"
                          size="small"
                        />
                      </div>
                      <div className="flex items-center">
                        <span className="mx-2 text-gray-300">|</span>
                        <span className="text-gray-500 mr-1">IFMV:</span>
                        <span>
                          {selectedProperty.ifmv != null && !isNaN(selectedProperty.ifmv)
                            ? `$${Number(selectedProperty.ifmv).toLocaleString()}`
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="mx-2 text-gray-300">|</span>
                        <span className="text-gray-500 mr-1">Taxes:</span>
                        <span>
                          {selectedProperty.taxes != null && !isNaN(selectedProperty.taxes)
                            ? `$${Number(selectedProperty.taxes).toLocaleString()}`
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                    <Tooltip title={salesWebScreenshot ? '' : 'Not Available'} arrow>
                      <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          disabled={!salesWebScreenshot}
                          onClick={() => setSalesWebOpen(true)}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            backgroundColor: '#4b5563',
                            '&:hover': { backgroundColor: '#374151' },
                            '&.Mui-disabled': {
                              backgroundColor: 'transparent !important',
                              color: 'rgba(0, 0, 0, 0.26) !important',
                              border: '1px solid rgba(0, 0, 0, 0.16) !important',
                              opacity: 0.72,
                              pointerEvents: 'none',
                            },
                          }}
                        >
                          Sales Web
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
  
                  {/* Remarks */}
                  <div className="px-3 py-2">
                    <p className="text-xs leading-snug">{selectedProperty.Remarks || "No MLS Remarks"}</p>
                  </div>
                </>
              )}
  
              {/* Inventory Tab */}
              {activeTab === subjectTabOffset + 1 && (
                <div className="p-3">
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    {[
                      ['Style', displayInventory(selectedProperty.style)],
                      ['Sqft', displayInventory(selectedProperty.sqft)],
                      ['Regrid footprint (sq ft)', displayInventory(selectedProperty.RegridBldgFtPrnt)],
                      ['Acres', displayInventory(selectedProperty.acres, 'acres')],
                      ['Year Built', displayInventory(selectedProperty.yearBuilt, 'year')],
                      ['Eff Year Built', displayInventory(selectedProperty.effYearBuilt, 'year')],
                      ['Full Baths', displayInventory(selectedProperty.fullBaths)],
                      ['Half Baths', displayInventory(selectedProperty.halfBaths)],
                      ['Bedrooms', displayInventory(selectedProperty.bedrooms)],
                      ['Kitchens', displayInventory(selectedProperty.kitchens)],
                      ['View', displayInventory(selectedProperty.view)],
                      ['Condition', displayInventory(selectedProperty.condition)],
                      ['Grade', displayInventory(selectedProperty.grade)],
                      ['Pool', displayInventory(selectedProperty.pool, 'flag')],
                      ['Basement', displayInventory(selectedProperty.basement)],
                      ['Garage', displayInventory(selectedProperty.garage)],
                      ['Fireplaces', displayInventory(selectedProperty.fireplaces)],
                      ['Deck / Patio / Porch', outdoorLabel(selectedProperty.deck, selectedProperty.patio, selectedProperty.porch)],
                      ['CAC', displayInventory(selectedProperty.cac, 'cac')],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <p className="text-xs text-gray-500">{label}</p>
                        <p className="text-sm">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* RAR History Tab */}
              {muniCode && activeTab === subjectTabOffset + 2 && (
                <div className="p-3">
                  <h3 className="text-sm font-semibold mb-2">RAR History for MuniCode: {muniCode}</h3>
                  {rarError ? (
                    <div className="text-red-500">{rarError}</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs border">
                        <thead>
                          <tr>
                            <th className="border px-2 py-1 bg-gray-100 text-left">Year</th>
                            <th className="border px-2 py-1 bg-gray-100 text-left">RAR</th>
                            <th className="border px-2 py-1 bg-gray-100 text-left">ProjectedRAR</th>
                            <th className="border px-2 py-1 bg-gray-100 text-left">EQ</th>
                            <th className="border px-2 py-1 bg-gray-100 text-left">Median Sale Px</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rarHistory.length === 0 ? (
                            <tr><td colSpan={5} className="text-gray-400 text-center py-2">No RAR history found.</td></tr>
                          ) : (
                            rarHistory.map((row, idx) => {
                              const medianPx = Number(row.MedianSalePrice);
                              return (
                                <tr key={idx} className="odd:bg-gray-50">
                                  <td className="border px-2 py-1">{row.Year}</td>
                                  <td className="border px-2 py-1">
                                    {row.RAR}
                                    {row.RARYoY && <span className="text-gray-600 ml-1 whitespace-nowrap">{row.RARYoY}</span>}
                                  </td>
                                  <td className="border px-2 py-1">
                                    {(() => {
                                      const rarDiffStr = row.ProjectedRARVsRAR;
                                      const hasRowStats =
                                        row.ProjectedRAR !== null &&
                                        row.ProjectedRAR !== undefined &&
                                        (row.ProjectedRARObservations !== undefined ||
                                          row.ProjectedRARmean !== undefined ||
                                          row.ProjectedRARstdev !== undefined);
                                      const showTooltip = rarDiffStr != null || hasRowStats;
                                      const body = (
                                        <>
                                          {typeof row.ProjectedRAR === 'number'
                                            ? Number(row.ProjectedRAR).toFixed(6).replace(/\.0+$/, '')
                                            : (row.ProjectedRAR ?? '')}
                                          {row.ProjectedRARYoY && (
                                            <span className="text-gray-600 ml-1 whitespace-nowrap">{row.ProjectedRARYoY}</span>
                                          )}
                                          {hasRowStats && (
                                            <InfoOutlinedIcon fontSize="small" className="text-gray-400 ml-1" />
                                          )}
                                        </>
                                      );
                                      if (!showTooltip) return body;
                                      return (
                                        <Tooltip
                                          disableInteractive
                                          title={
                                            <Box>
                                              {rarDiffStr != null && (
                                                <Box className="mb-1" sx={{ whiteSpace: 'nowrap' }}>
                                                  <div>
                                                    <b>RAR % Diff</b>:{' '}
                                                    <Box component="span" sx={{ fontWeight: 600 }}>
                                                      {rarDiffStr}
                                                    </Box>
                                                  </div>
                                                  <Box sx={{ fontSize: 11, opacity: 0.85, mt: 0.5 }}>
                                                    (Proj − RAR) / RAR
                                                  </Box>
                                                </Box>
                                              )}
                                              {hasRowStats && (
                                                <Box sx={{ mt: rarDiffStr != null ? 0.75 : 0 }}>
                                                  <div>
                                                    Observations:{' '}
                                                    {typeof row.ProjectedRARObservations === 'number'
                                                      ? Number(row.ProjectedRARObservations).toFixed(6).replace(/\.0+$/, '')
                                                      : (row.ProjectedRARObservations ?? 'N/A')}
                                                  </div>
                                                  <div>
                                                    Mean:{' '}
                                                    {typeof row.ProjectedRARmean === 'number'
                                                      ? Number(row.ProjectedRARmean).toFixed(4).replace(/\.0+$/, '')
                                                      : (row.ProjectedRARmean ?? 'N/A')}
                                                  </div>
                                                  <div>
                                                    Std Dev:{' '}
                                                    {typeof row.ProjectedRARstdev === 'number'
                                                      ? Number(row.ProjectedRARstdev).toFixed(4).replace(/\.0+$/, '')
                                                      : (row.ProjectedRARstdev ?? 'N/A')}
                                                  </div>
                                                </Box>
                                              )}
                                            </Box>
                                          }
                                          slotProps={rarHistoryTooltipSlotProps}
                                          arrow
                                          placement="top"
                                        >
                                          <span className="inline-flex items-center flex-wrap gap-x-0.5 cursor-default">
                                            {body}
                                          </span>
                                        </Tooltip>
                                      );
                                    })()}
                                  </td>
                                  <td className="border px-2 py-1">
                                    {row.EQ}
                                    {row.EQYoY && <span className="text-gray-600 ml-1 whitespace-nowrap">{row.EQYoY}</span>}
                                  </td>
                                  <td className="border px-2 py-1">
                                    {row.MedianSalePrice != null && Number.isFinite(medianPx) ? `$${Math.round(medianPx).toLocaleString()}` : ''}
                                    {row.MedianSalePriceYoY && <span className="text-gray-600 ml-1 whitespace-nowrap">{row.MedianSalePriceYoY}</span>}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
  
          {/* Navigation Buttons */}
          {selectedProperties.length > 1 && (
            <div className="flex justify-between items-center p-3 border-t bg-gray-50">
              <button
                onClick={onPrevious}
                disabled={currentPropertyIndex === 0}
                className="px-3 py-1 bg-white rounded hover:bg-gray-100 disabled:opacity-50 border"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">
                {currentPropertyIndex + 1} of {selectedProperties.length}
              </span>
              <button
                onClick={onNext}
                disabled={currentPropertyIndex === selectedProperties.length - 1}
                className="px-3 py-1 bg-white rounded hover:bg-gray-100 disabled:opacity-50 border"
              >
                Next
              </button>
            </div>
          )}
  
          {/* Enlarged Photo Dialog - shared with PhotoCell's grid-cell photo preview */}
          <PhotoDetailDialog
            open={isPhotoPopupOpen}
            onClose={closePhotoPopup}
            photoUrl={photoUrl}
            imgLoading={photoDetailLoading}
            onImgLoad={() => setPhotoDetailLoading(false)}
            onImgError={() => setPhotoDetailLoading(false)}
            nav={hasMlsPhotos ? { onPrev: photoCarousel.goPrev, onNext: photoCarousel.goNext, current: photoCarousel.displayNumber, total: photoCarousel.total } : null}
            emptyState={photoDetailEmptyState}
            dragActive={dragActive}
            dragHandlers={dragHandlers}
            primaryAction={photoUrl ? {
              label: isUploadingPhoto ? 'Saving...' : (isLivePhoto ? 'Upload New Photo' : 'Set as Primary Photo'),
              onClick: handlePrimaryPhotoAction,
              disabled: isUploadingPhoto,
            } : null}
            secondaryAction={(isLivePhoto && photoUrl) ? {
              label: 'Delete photo',
              onClick: () => setDeleteConfirmOpen(true),
              disabled: isUploadingPhoto,
            } : null}
            dialogProps={{ container: () => document.body, style: { zIndex: 10001 } }}
          />

          {/* Upload Photo Dialog */}
          <PhotoUploadDialog
            open={uploadDialogOpen}
            onClose={() => setUploadDialogOpen(false)}
            onUpload={handlePhotoUpload}
            uploading={isUploadingPhoto}
            dialogProps={{ container: () => document.body, style: { zIndex: 10001 } }}
          />

          {/* Delete Photo Confirmation Dialog */}
          <PhotoDeleteConfirmDialog
            open={deleteConfirmOpen}
            onClose={() => setDeleteConfirmOpen(false)}
            onConfirm={handlePhotoDelete}
            dialogProps={{ container: () => document.body, style: { zIndex: 10001 } }}
          />
        </div>
      </div>
    );
  };
  
  export default PropertyRecordCard;